import type { TranslationModule } from '../types';

export const crmSectionTranslations: TranslationModule = {
  ro: {
    badge: "NOU",
    title: "CRM Complex Online",
    subtitle: "Platforma ta completă de gestionare a dosarelor și a activității de cabinet.",
    checklist: [
      "Sincronizare automată cu instanțele (Portalul Just)",
      "Managementul termenelor și al calendarului",
      "Arhivă digitală și căutare inteligentă",
      "Facturare, încasări și pontaj pe dosar",
      "Task-uri, colaborare și rapoarte avansate"
    ],
    btnCTA: "Accesează CRM-ul",
    btnMore: "Află mai multe",
    cards: {
      sync: {
        title: "Sincronizare automată cu instanțele",
        desc: "Preluare automată: Extrage termenele, soluțiile pe scurt și modificările direct de pe portalul instanțelor. Alerte în timp real: Trimite notificări (e-mail sau SMS) când apar modificări în dosarele monitorizate. Istoric complet: Salvează automat toate evoluțiile și soluțiile pronunțate în dosar."
      },
      terms: {
        title: "Termene & Calendar",
        desc: "Calcul automat: Calculează termenele procedurale (pe zile libere, luni, ani) conform Codului de Procedură Civilă. Alerte timpurii: Notifică utilizatorul cu câteva zile înainte de expirarea unui termen limită (ex: depunere întâmpinare). Sincronizare externă: Se conectează bidirecțional cu Google Calendar, Outlook sau Apple Calendar."
      },
      archive: {
        title: "Arhivă Digitală",
        desc: "Dosar virtual: Grupează toate documentele, clienții, notele și sarcinile într-un singur loc. Căutare rapidă: Permite găsirea instantanee a unui document după cuvinte cheie, nume client sau număr de dosar. Controlul accesului: Permite partajarea securizată a documentelor cu membrii echipei sau direct cu clientul."
      },
      billing: {
        title: "Timp & Facturare",
        desc: "Pontaj pe dosar: Înregistrează secundă cu secundă timpul petrecut pentru redactare, consultanță sau prezență în instanță. Urmărire încasări: Monitorizează restanțele și trimite notificări automate de plată către clienți."
      },
      tasks: {
        title: "Task-uri & Colaborare",
        desc: "Delegare sarcini: Permite alocarea de task-uri specifice către avocații stagiari sau colaboratori. Rapoarte de activitate: Generează statistici privind performanța echipei și profitabilitatea per dosar."
      },
      gdrive: {
        title: "Stocare Securizată Google Drive (100% Privat)",
        desc: "Prin utilizarea modului „CRM Complex Online”, datele dosarelor sunt stocate în cloud-ul Google Drive propriu utilizatorului, ceea ce asigură o descentralizare a bazei de date și confidențialitatea datelor. Noi nu avem acces la datele dumneavoastră!"
      },
      gcal: {
        title: "Sincronizare Google Calendar",
        desc: "Sincronizează termenele și sarcinile direct cu contul tău Google Calendar. Conectarea securizată îți aduce toate termenele limită, ședințele și activitățile CRM pe telefon, cu alerte și mementouri automate, pentru a nu rata nicio înfățișare."
      }
    },
    benefits: [
      "Date securizate și backup în cloud",
      "Acces de oriunde, pe orice dispozitiv",
      "Notificări e-mail și SMS în timp real"
    ]
  },
  en: {
    badge: "NEW",
    title: "Complex CRM Online",
    subtitle: "Your complete platform for managing legal cases and law firm activities.",
    checklist: [
      "Automatic synchronization with courts (Portalul Just)",
      "Procedural deadlines and calendar management",
      "Digital archive and intelligent search",
      "Time tracking, invoicing and collections",
      "Task delegation, collaboration and advanced reporting"
    ],
    btnCTA: "Access CRM",
    btnMore: "Learn more",
    cards: {
      sync: {
        title: "Automatic Court Sync",
        desc: "Auto-fetch: Extracts hearings, brief rulings, and updates directly from court portals. Real-time alerts: Sends notifications (email or SMS) when changes occur in monitored cases. Complete history: Automatically saves all case developments and rulings."
      },
      terms: {
        title: "Deadlines & Calendar",
        desc: "Auto-calculation: Computes procedural deadlines (in calendar/business days, months, years) according to the Civil Procedure Code. Early alerts: Notifies the user days before a deadline expires (e.g., filing statements). External sync: Two-way syncs with Google Calendar, Outlook, or Apple Calendar."
      },
      archive: {
        title: "Digital Archive",
        desc: "Virtual folder: Groups all documents, clients, notes, and tasks in a single secure place. Rapid search: Instantly find any document by keyword, client name, or case number. Access control: Allows secure document sharing with team members or clients."
      },
      billing: {
        title: "Time & Billing",
        desc: "Timesheet by case: Records second-by-second time spent on writing, consulting, or court attendance. Income tracking: Monitors unpaid bills and sends automated payment notifications to clients."
      },
      tasks: {
        title: "Tasks & Collaboration",
        desc: "Task delegation: Assigns specific tasks to trainee lawyers or external collaborators. Activity reports: Generates performance statistics for the team and profitability metrics per case."
      },
      gdrive: {
        title: "Secure Google Drive Storage (100% Private)",
        desc: "By using the 'CRM Complex Online' module, all case data is stored in the user's own Google Drive cloud, ensuring database decentralization and maximum privacy. We do not have access to your data!"
      },
      gcal: {
        title: "Google Calendar Integration",
        desc: "Synchronize deadlines and tasks directly with your personal Google Calendar account. The secure connection displays all CRM activities on your mobile device with real-time reminders."
      }
    },
    benefits: [
      "Secure data & cloud backup",
      "Access anywhere, on any device",
      "Real-time email and SMS notifications"
    ]
  },
  fr: {
    badge: "NOUVEAU",
    title: "CRM Complexe en Ligne",
    subtitle: "Votre plateforme complète pour la gestion des dossiers et des activités du cabinet.",
    checklist: [
      "Synchronisation automatique avec les tribunaux (Portalul Just)",
      "Gestion des délais de procédure et calendrier",
      "Archive numérique et recherche intelligente",
      "Saisie du temps, facturation et encaissements",
      "Gestion des tâches, collaboration et rapports avancés"
    ],
    btnCTA: "Accéder au CRM",
    btnMore: "En savoir plus",
    cards: {
      sync: {
        title: "Synchronisation automatique des tribunaux",
        desc: "Récupération automatique: Extrait les audiences, les décisions et les mises à jour directement. Alertes en temps réel: Envoie des notifications (e-mail ou SMS) en cas de modification. Historique complet: Enregistre automatiquement tous les développements du dossier."
      },
      terms: {
        title: "Délais & Calendrier",
        desc: "Calcul automatique: Calcule les délais de procédure conformément au code de procédure civile. Alertes précoces: Notifie l'utilisateur quelques jours avant l'expiration du délai. Synchronisation externe: Se connecte avec Google Calendar, Outlook ou Apple Calendar."
      },
      archive: {
        title: "Archive Numérique",
        desc: "Dossier virtuel: Regroupe les documents, clients, notes et tâches en un seul endroit. Recherche rapide: Permet de trouver instantanément un document par mot-clé, nom de client ou numéro de dossier. Contrôle d'accès: Permet un partage sécurisé avec l'équipe ou le client."
      },
      billing: {
        title: "Temps & Facturation",
        desc: "Pointage par dossier: Enregistre la durée exacte passée sur la rédaction, le conseil ou l'audience. Suivi des encaissements: Surveille les impayés et envoie des rappels automatiques aux clients."
      },
      tasks: {
        title: "Tâches & Collaboration",
        desc: "Délégation de tâches: Permet d'attribuer des tâches spécifiques aux collaborateurs. Rapports d'activité: Génère des statistiques sur les performances et la rentabilité."
      },
      gdrive: {
        title: "Stockage Sécurisé Google Drive (100% Privé)",
        desc: "En utilisant le module « CRM Complexe en Ligne », les données sunt stocarea sur le propre Google Drive de l'utilisateur, assurant la décentralisation de la base de données. Nous n'avons pas accès à vos données !"
      },
      gcal: {
        title: "Intégration Google Calendar",
        desc: "Synchronisez vos échéances et tâches directement avec votre compte Google Calendar. Cette connexion sécurisée apporte tous les rappels CRM sur votre mobile en temps réel."
      }
    },
    benefits: [
      "Données sécurisées et sauvegarde cloud",
      "Accès de n'importe où, sur tout appareil",
      "Notifications email et SMS en temps réel"
    ]
  }
};

export default crmSectionTranslations;
